/**
 * @Copyright 2014-2019 DataVoke, Inc. All rights reserved.
 */

'use strict';

import {
    applyUnknownProperties,
    ClientProperties,
    EntityBase,
    EntityDefinitionBase
} from "./entitybase";
import {EntityTypes} from "../utils/dvtypes";
import {tempID} from "../utils/tools";

class RecordTypeDefinition extends EntityDefinitionBase {
    /**
     * Create a new Record Type Definition instance
     * @param {Object|Null} [defaults = {}] - (OPTIONAL) Default object (transmitted definition object?) to use for properties
     * @param {boolean} [defaults.AllowRecCodeChanges = true] - TRUE, allow record code to be modified; FALSE, record code cannot be modified
     * @param {boolean} [defaults.CascadeDelete = false] - TRUE, deletes of records will delete any properties in other records linked to this record when deleted; FALSE, do not delete other record property values
     * @param {EntityLinker} [defaults.CodePropertyID]
     * @param {string} [defaults.Comment]
     * @param {boolean} [defaults.IncludeDataInPackage = false] - TRUE, include data for this record type if packaged; FALSE, do not include data
     * @param {number} [defaults.Incr = 0] - The amount to increment for each new serial
     * @param {string} [defaults.Label = '']
     * @param {Array} [defaults.RTsToDelete]
     * @param {string} [defaults.RecordCodeLabel]
     * @param {boolean} [defaults.RestrictQuickList] - TRUE, prevent allowing creation of a quick list for this record type; FALSE, allow using to create a quick list
     * @param {EntityLinker} [defaults.SerialPropertyID]
     * @param {string} [defaults.iconResourcePath = "recordtypeicons/record"]
     */
    constructor (defaults = {}) {
        if (defaults == null) {
            // this can happen if "null" is actually passed as "defaults"
            defaults = {};
        }

        super(defaults);

        /**
         * Name for the record type definition
         * @type {string}
         */
        this.Name = this.Name || "New RecordType";

        /**
         * Flag to allow the record code to be changed after value creation
         * @type {boolean}
         */
        this.AllowRecCodeChanges = defaults.AllowRecCodeChanges || true;

        /**
         * Flag to indicate if cascade deletes should be performed for deleted values
         * @type {boolean}
         */
        this.CascadeDelete = defaults.CascadeDelete || false;

        /**
         * The entity linker to the property that this record types code property
         * @type {EntityLinker}
         */
        this.CodePropertyID = defaults.CodePropertyID || undefined;

        /**
         * Optional comment for this record type
         * @type {string|null}
         */
        this.Comment = defaults.Comment || null;

        /**
         * Flag to indicate if data for this record type should be included in packages that have this record type
         * @type {boolean}
         */
        this.IncludeDataInPackage = defaults.IncludeDataInPackage || false;

        /**
         * The amount to increment the serial value, if necessary
         * @type {number}
         */
        this.Incr = defaults.Incr || 0;

        /**
         * Label for this record type
         * @type {string}
         */
        this.Label = defaults.Label || "";

        /**
         * Array of other record types to delete when a record is deleted from this record type
         * @type {Array|undefined}
         */
        this.RTsToDelete = defaults.RTsToDelete || undefined;

        /**
         * Label for the record code property
         * @type {string|undefined}
         */
        this.RecordCodeLabel = defaults.RecordCodeLabel || undefined;

        /**
         * Flag to indicate if quick lists should be prohibited from using this record type
         * @type {boolean}
         */
        this.RestrictQuickList = defaults.RestrictQuickList || false;

        /**
         * Optional entity linker to the property that holds serial values for this record type
         * @type {EntityLinker|null}
         */
        this.SerialPropertyID = defaults.SerialPropertyID || null;

        /**
         * Entity icon path from the resource library for this record type
         * @type {string}
         */
        this.iconResourcePath = defaults.iconResourcePath || "recordtypeicons/record";

        // allow for any entity definition properties not known by the class
        applyUnknownProperties(this, defaults);
    }
}

export class RecordTypeEntity extends EntityBase {
    /**
     * Create a new Record Type entity instance
     * @param {Object|Null} [defaults = {}] - (OPTIONAL) Default object (transmitted definition object?) to use for properties
     * @param {number} [defaults.RecordTypeID] - Optional record type ID number for this record type entity
     * @param {number} [defaults.LastSerial = 0] - Optional serial number for the next record of this record type entity
     * @param {Object} [defaults.Definition = {}] - Default object for properties of the record type entity definition
     */
    constructor (defaults = {}) {
        if (defaults == null) {
            // this can happen if "null" is actually passed as "defaults"
            defaults = {};
        }

        // ensure that the definition property of the defaults is an object.
        if (defaults.Definition && typeof defaults.Definition === 'string') {
            defaults.Definition = JSON.parse(defaults.Definition);
        }

        super(defaults);

        /**
         * Name for this record type entity
         * @type {string}
         */
        this.Name = this.Name || "New_RecordType";

        /**
         * Retrieve or generate the record type ID value for this record type entity
         * @type {number}
         */
        this.RecordTypeID = defaults.RecordTypeID || tempID();

        /**
         * The serial number for the next record of this record type entity
         * @type {number}
         */
        this.LastSerial = defaults.LastSerial || 0;

        /**
         * Set the definition for this record type entity
         * @type {RecordTypeDefinition}
         */
        this.Definition = new RecordTypeDefinition(defaults.Definition);

        /**
         * Set the client properties for this entity type
         * @type {ClientProperties}
         */
        this.clientProperties = new ClientProperties(EntityTypes.RecordType);
    }
}
